/**
 * FAQ.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 * Updated: Nov 10, 2025 - Fixed FAQPage structured data for Google Search Console
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import EditablePage from './EditablePage';
import SEO from './SEO';
import { useLanguage } from '../context/LanguageContext';
import { getTranslatedPage } from '../i18n/pages';

export default function FAQ() {
  const { language } = useLanguage();
  const tr = getTranslatedPage('faq', language);
  // Structured data for FAQPage - Required by Google
  // Update these questions/answers to match your actual FAQ content
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I order from Cranford Publications?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You can order directly through our website by browsing our catalog of Celtic fiddle music books and CDs. Add items to your cart and proceed to checkout. We accept major credit cards and PayPal. You can also contact us by phone at 1-902-929-2391  or email at cranfordpub@gmail.com."
        }
      },
      {
        "@type": "Question",
        "name": "What shipping options do you offer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We ship worldwide from Nova Scotia, Canada. Shipping options include standard international mail and expedited services. Delivery times vary by location: typically 1-2 weeks for North America and 2-4 weeks for international orders. Shipping costs are calculated at checkout based on your location and order size."
        }
      },
      {
        "@type": "Question",
        "name": "What payment methods do you accept?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We accept all major credit cards (Visa, MasterCard, American Express) and PayPal. All payments are processed securely through Stripe payment gateway. We do not store credit card information on our servers."
        }
      },
      {
        "@type": "Question",
        "name": "What is your return policy?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We offer a 30-day satisfaction guarantee. If you're not completely satisfied with your purchase, you can return it within 30 days for a full refund or exchange. Items must be in original condition. Please contact us before returning any items to receive return authorization and instructions."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer digital downloads?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, many of our products are available as digital downloads including sheet music, ABC notation files, and MP3 audio recordings. Digital products are delivered instantly after purchase via email with download links."
        }
      },
      {
        "@type": "Question",
        "name": "What music traditions do you specialize in?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Cranford Publications specializes in Celtic fiddle music, with a particular focus on Cape Breton, Irish, and Scottish traditional music. Our collection includes fiddle tunes, teaching materials, recordings, and historical resources for traditional Celtic music."
        }
      }
    ]
  };

  return (
    <>
      <SEO 
        title={`${tr?.title || 'FAQ - Frequently Asked Questions'} | Cranford Publications`}
        description={tr?.description || 'Get answers to common questions about ordering Celtic fiddle music books and CDs from Cranford Publications. Shipping, payment, returns, and product information.'}
        canonical="/faq"
        keywords="fiddle music FAQ, Celtic music shop questions, ordering info, shipping info, Cranford Publications help"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <EditablePage pageId="faq" pageTitle={tr?.title || 'Frequently Asked Questions'} />
    </>
  );
}